/**
 * Share Tool Modal
 * Allows users to share tools via:
 * - RingCentral user search & direct message
 * - Open in RingCentral app (deep link)
 * - Copy link
 * - Email
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Link, Mail, MessageSquare, Search, Send, User, ExternalLink } from 'lucide-react';
import { ringCentralAPI } from '../../../services/api';
import '../AIGarage.css';

const ShareToolModal = ({ isOpen, tool, userEmail, onClose }) => {
  const [shareSuccess, setShareSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Debug log
  useEffect(() => {
    console.log('ShareToolModal rendered:', { isOpen, tool: tool?.title });
  }, [isOpen, tool]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setShareSuccess(false);
      setCopySuccess(false);
    }
  }, [isOpen]);

  const handleCopyLink = () => {
    const url = tool.url || `${window.location.origin}/ai-garage/tool/${tool.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch(err => {
      console.error('Copy failed:', err);
    });
  };

  const handleOpenInRC = async () => {
    try {
      // 1. Wait for clipboard copy to complete (ensures clipboard is ready before app opens)
      const toolUrl = tool.url || `${window.location.origin}/ai-garage/tool/${tool.id}`;
      await navigator.clipboard.writeText(toolUrl);

      // 2. Then open RingCentral Team Messaging app with short pre-filled message
      // Use shorter message to avoid encoding issues - user will paste the URL from clipboard
      const shortMessage = `🚀 Check out this AI Garage tool: ${tool.title}`;
      const encodedMessage = encodeURIComponent(shortMessage);
      // Fixed: Use Team Messaging deep link instead of SMS
      const deepLink = `rcapp://conversation?action=new&body=${encodedMessage}`;
      window.location.href = deepLink;

      // 3. Show success message (only after clipboard succeeds)
      setShareSuccess(true);
      setTimeout(() => {
        setShareSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      // Fallback: Still try to open RC app even if clipboard fails
      const shortMessage = `🚀 Check out this AI Garage tool: ${tool.title}`;
      const encodedMessage = encodeURIComponent(shortMessage);
      const deepLink = `rcapp://conversation?action=new&body=${encodedMessage}`;
      window.location.href = deepLink;
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal-container garage-share-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="garage-modal-header">
          <h2 className="garage-modal-title">Share Tool</h2>
          <button className="garage-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tool Preview */}
        <div className="garage-share-tool-preview">
          <h3>{tool.title}</h3>
          <p>{tool.description?.substring(0, 100)}{tool.description?.length > 100 ? '...' : ''}</p>
        </div>

        <div className="garage-modal-body">
          {/* Main Share Option - RingCentral Deep Link */}
          <div className="garage-share-deeplink">
            <p className="garage-share-hint">
              <MessageSquare size={20} />
              Opens RingCentral Team Messaging with the tool link copied to your clipboard. Search for a colleague and paste the link to share.
            </p>
            <button
              onClick={handleOpenInRC}
              className="garage-btn garage-btn-primary garage-btn-large"
            >
              <ExternalLink size={18} />
              Open RingCentral & Copy Link
            </button>
          </div>

          {/* Divider */}
          <div className="garage-share-divider">
            <span>or</span>
          </div>

          {/* Alternative Option - Just Copy Link */}
          <div className="garage-share-other-options">
            <button onClick={handleCopyLink} className="garage-share-option-btn">
              <Link size={18} />
              Copy Link Only
            </button>
          </div>

          {/* Success Messages */}
          {shareSuccess && (
            <div className="garage-share-success">
              ✅ Link copied! Opening RingCentral app...
            </div>
          )}
          {copySuccess && (
            <div className="garage-share-success">
              ✅ Link copied to clipboard!
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ShareToolModal;
